'use client';

import { c as _c } from "react/compiler-runtime";
import type { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import type { AppKitNetwork } from '@reown/appkit/networks';
import type { QueryClient } from '@tanstack/react-query';
import { ApiController, BlockchainApiController, OptionsController } from '@reown/appkit-controllers';
import { useAppKitTheme } from '@reown/appkit/react';
import { useIsMounted } from '@repo/hooks';
import { type PropsWithChildren, useEffect, useState } from 'react';
import { cookieToInitialState, createConfig, createStorage, noopStorage, type Config as WagmiConfig, WagmiProvider } from 'wagmi';
import type { AppKitConnectionMode } from './create-appkit-for-connection-mode';
import { FEATURED_WALLET_IDS } from '../configs/featured-wallets';
import { isE2E_Next } from '../configs/process';
import { createAppKitForConnectionMode } from './create-appkit-for-connection-mode';
import { ReactQueryProvider } from './ReactQueryProvider';
import { UserAccountProvider } from './UserAccountProvider';
export type { AppKitConnectionMode } from './create-appkit-for-connection-mode';
type CreateAppKitOptions = Parameters<typeof createAppKitForConnectionMode>[0];

// AppKit fetches ENS identity on connect and renders the avatar URL as an
// <img> in the modal. The URL can point at arbitrary hosts (IPFS gateways,
// NFT media), which CSP blocks. Strip avatar so the modal uses its gradient.
const _originalFetchIdentity = BlockchainApiController.fetchIdentity;
const _originalFetchUsage = ApiController.fetchUsage;
async function fetchIdentityWithoutAvatar(request: Parameters<typeof BlockchainApiController.fetchIdentity>[0]) {
  const identity = await _originalFetchIdentity.call(BlockchainApiController, request);
  return {
    ...identity,
    avatar: null
  };
}
function configureAppKitNetworkAccess(connectionMode: AppKitConnectionMode): void {
  if (connectionMode === 'explicit') {
    BlockchainApiController.fetchIdentity = async () => ({
      avatar: null,
      name: null
    });
    ApiController.fetchUsage = async () => undefined;
    return;
  }
  BlockchainApiController.fetchIdentity = fetchIdentityWithoutAvatar;
  ApiController.fetchUsage = () => _originalFetchUsage.call(ApiController);
}
function configureAppKitConnectorDiscovery(connectionMode: AppKitConnectionMode): void {
  // The Wagmi adapter reads these from controller state. `createAppKit` has no `enableBaseAccount`
  // option, so set both before `createAppKit`, and restore them for normal discovery because the
  // controller is a module-level singleton.
  // https://github.com/reown-com/appkit/pull/5655
  OptionsController.setOptions({
    ...OptionsController.state,
    enableBaseAccount: connectionMode !== 'explicit',
    enableInjected: connectionMode !== 'explicit'
  });
}
type ThemeMode = 'light' | 'dark' | 'system';
function resolveThemeFromDOM(themeMode: ThemeMode): 'light' | 'dark' {
  const root = document.documentElement;
  if (root.classList.contains('dark')) return 'dark';
  if (root.classList.contains('light')) return 'light';
  // No theme class on <html> (an app that never stamps one). Fall back per
  // `themeMode`: 'system' follows the OS preference; otherwise use the fixed
  // value — the default 'light' preserves the historical classless behavior so
  // adopting this provider never flips a light-only app to dark on a dark OS.
  if (themeMode === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return themeMode;
}
function AppKitThemeSync(t0) {
  const $ = _c(4);
  const {
    themeMode
  } = t0;
  const {
    setThemeMode
  } = useAppKitTheme();
  let t1;
  let t2;
  if ($[0] !== setThemeMode || $[1] !== themeMode) {
    t1 = () => {
      setThemeMode(resolveThemeFromDOM(themeMode));
      const observer = new MutationObserver(() => {
        setThemeMode(resolveThemeFromDOM(themeMode));
      });
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"]
      });
      let mediaQuery;
      const onMediaChange = () => setThemeMode(resolveThemeFromDOM(themeMode));
      if (themeMode === "system") {
        mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        mediaQuery.addEventListener("change", onMediaChange);
      }
      return () => {
        observer.disconnect();
        mediaQuery?.removeEventListener("change", onMediaChange);
      };
    };
    t2 = [setThemeMode, themeMode];
    $[0] = setThemeMode;
    $[1] = themeMode;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
export function Web3Provider(t0) {
  const $ = _c(21);
  const {
    children,
    wagmiAdapter,
    wagmiConfig,
    cookies,
    disableAppKit: t1,
    ssr: t2,
    enableAppKitAnalytics,
    metadata,
    featuredWalletIds: t3,
    screenAddressEndpoint,
    queryClient,
    themeMode: t4,
    connectionMode: t5
  } = t0;
  const disableAppKit = t1 === undefined ? false : t1;
  const ssr = t2 === undefined ? false : t2;
  const featuredWalletIds = t3 === undefined ? FEATURED_WALLET_IDS : t3;
  const themeMode = t4 === undefined ? "light" : t4;
  const connectionMode = t5 === undefined ? "discovery" : t5;
  if (connectionMode === "explicit" && wagmiAdapter.wagmiConfig !== wagmiConfig) {
    throw new Error("Web3Provider: explicit mode requires one shared Wagmi config object");
  }
  let t6;
  if ($[0] !== cookies || $[1] !== wagmiConfig) {
    t6 = cookieToInitialState(wagmiConfig, cookies);
    $[0] = cookies;
    $[1] = wagmiConfig;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const initialState = t6;
  const rendersOnServer = ssr && connectionMode !== "explicit";
  let t7;
  if ($[3] !== rendersOnServer || $[4] !== wagmiConfig) {
    t7 = () => typeof window === "undefined" && rendersOnServer ? createConfig({
      chains: wagmiConfig.chains,
      client: t8 => {
        const {
          chain
        } = t8;
        return wagmiConfig.getClient({
          chainId: chain.id
        });
      },
      connectors: wagmiConfig.connectors.map(_temp),
      multiInjectedProviderDiscovery: false,
      ssr: true,
      storage: createStorage({
        storage: noopStorage
      })
    }) : undefined;
    $[3] = rendersOnServer;
    $[4] = wagmiConfig;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const [serverRenderConfig] = useState(t7);
  const providerConfig = serverRenderConfig ?? wagmiConfig;
  const isMounted = useIsMounted();
  if ((!ssr || connectionMode === "explicit") && !isMounted) {
    return null;
  }
  if (typeof window !== "undefined") {
    configureAppKitNetworkAccess(connectionMode);
  }
  if (!disableAppKit && typeof window !== "undefined") {
    configureAppKitConnectorDiscovery(connectionMode);
    const sanitizedChains = wagmiAdapter.wagmiConfig.chains.map(_temp2);
    createAppKitForConnectionMode({
      adapters: [wagmiAdapter],
      projectId: connectionMode === "explicit" ? "" : wagmiAdapter.projectId ?? "",
      networks: sanitizedChains as unknown as [AppKitNetwork, ...AppKitNetwork[]],
      defaultNetwork: sanitizedChains[0],
      ...(metadata !== undefined ? {
        metadata
      } : {}),
      ...(connectionMode === "explicit" ? {} : {
        featuredWalletIds
      }),
      enableWalletGuide: false,
      enableCoinbase: false,
      ...(connectionMode === "explicit" ? {
        allWallets: "HIDE" as const,
        enableEIP6963: false,
        enableInjected: false,
        enableWallets: false,
        enableWalletConnect: false,
        manualWCControl: true
      } : {}),
      allowUnsupportedChain: true,
      themeVariables: {
        "--w3m-font-family": "'FK Grotesk', ui-sans-serif, system-ui, sans-serif"
      },
      features: {
        email: false,
        socials: false,
        legalCheckbox: connectionMode !== "explicit",
        swaps: false,
        onramp: false,
        send: false,
        ...(connectionMode === "explicit" ? {
          allWallets: false,
          analytics: false,
          history: false,
          receive: false
        } : enableAppKitAnalytics !== undefined ? {
          analytics: enableAppKitAnalytics
        } : {})
      }
    }, connectionMode);
  }
  const reconnectOnMount = !isE2E_Next;
  let t8;
  if ($[6] !== disableAppKit || $[7] !== isMounted || $[8] !== themeMode) {
    t8 = !disableAppKit && isMounted && <AppKitThemeSync themeMode={themeMode} />;
    $[6] = disableAppKit;
    $[7] = isMounted;
    $[8] = themeMode;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== children || $[11] !== screenAddressEndpoint || $[12] !== t8) {
    t9 = <UserAccountProvider screenAddressEndpoint={screenAddressEndpoint}>{t8}{children}</UserAccountProvider>;
    $[10] = children;
    $[11] = screenAddressEndpoint;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== initialState || $[15] !== providerConfig || $[16] !== t9) {
    t10 = <WagmiProvider config={providerConfig} initialState={initialState} reconnectOnMount={reconnectOnMount}>{t9}</WagmiProvider>;
    $[14] = initialState;
    $[15] = providerConfig;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== queryClient || $[19] !== t10) {
    t11 = <ReactQueryProvider client={queryClient}>{t10}</ReactQueryProvider>;
    $[18] = queryClient;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}
function _temp2(chain_0) {
  const {
    custom: _custom,
    ...rest
  } = chain_0;
  return rest;
}
function _temp(connector) {
  return () => ({
    ...connector,
    setup: undefined
  });
}